import { call } from '@rental/contracts/client'
import { DAY_RATES_MAX_MIN_DAYS, DAY_RATES_MAX_PRICE_CENTS } from '@rental/domain'
import { format, formatDate, formatEuro } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { type FormEvent, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { DataTable } from '../components/DataTable'
import { Dialog } from '../components/Dialog'
import { FIELD_ROW, PAIRED_FIELD, ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { centsForInput } from '../money'
import type { StaffProblem } from '../problems'
import { minDaysOf, priceCentsOf } from './bulk'
import { conflictCell, type DayValues, NOTHING_SET } from './cell'

/** A day the Owner opened: its category, its date, what it holds and the version read. */
export interface AskedDay {
  categoryId: string
  categoryName: string
  day: string
  values: DayValues
  /** Null while the day has no cell. */
  version: number | null
}

/** What the dialog holds, as typed. */
interface Draft {
  price: string
  minPickup: string
  minThrough: string
  stopSale: boolean
}

/** The day as someone else left it, read from a refusal, and the version to send over it. */
type Conflict = { now: DayValues; version: number | null }

function draftOf(values: DayValues, locale: 'it' | 'en'): Draft {
  return {
    price: values.priceCents === null ? '' : centsForInput(values.priceCents, locale),
    minPickup: values.minDaysPickup === null ? '' : String(values.minDaysPickup),
    minThrough: values.minDaysThrough === null ? '' : String(values.minDaysThrough),
    stopSale: values.stopSale,
  }
}

/** A field's value, or why it cannot be sent: empty is "nothing set". */
const read = <T,>(text: string, parse: (text: string) => T | null) =>
  text.trim() === '' ? { ok: true as const, value: null } : parseOrFail(parse(text))
const parseOrFail = <T,>(value: T | null) =>
  value === null ? { ok: false as const } : { ok: true as const, value }

/**
 * W2-S S1: one day of one category: its price in euros (a comma for the
 * decimals), its two minimums and stop sale. It sends the version read; a day
 * changed meanwhile is refused (409), and the dialog then shows the day as it
 * is now beside what was typed, keeps what was typed, and offers to save over
 * it with the new version, or to close. Opened each time it is handed a day.
 */
export function DayDialog({
  dialogRef,
  asked,
  onSaved,
  onConflict,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  asked: AskedDay | null
  /** After the dialog has closed. */
  onSaved: (day: AskedDay) => void
  /** Someone changed the day meanwhile: the grid reads it again. */
  onConflict: () => void
}) {
  const { locale, messages: m } = useLocale()
  const words = m.prices
  const { takeOver } = useAccess()
  // Each opening starts from the day as it was read, whatever was typed last time.
  const [opened, setOpened] = useState<AskedDay | null>(null)
  const [draft, setDraft] = useState<Draft>(() => draftOf(asked?.values ?? NOTHING_SET, locale))
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const [conflict, setConflict] = useState<Conflict | null>(null)
  if (asked !== opened) {
    setOpened(asked)
    setDraft(draftOf(asked?.values ?? NOTHING_SET, locale))
    setTried(false)
    setProblem(null)
    setConflict(null)
  }
  useLayoutEffect(() => {
    if (asked && !dialogRef.current?.open) dialogRef.current?.showModal()
  }, [asked, dialogRef])
  // A refusal takes the button that was pressed away: the focus goes to the one that replaces it.
  const actions = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (conflict) actions.current?.querySelector<HTMLButtonElement>('[data-save-over]')?.focus()
  }, [conflict])

  const price = read(draft.price, priceCentsOf)
  const minPickup = read(draft.minPickup, minDaysOf)
  const minThrough = read(draft.minThrough, minDaysOf)
  const maxPrice = formatEuro(DAY_RATES_MAX_PRICE_CENTS, locale)
  const minError = format(words.minInvalid, { max: DAY_RATES_MAX_MIN_DAYS })

  async function save(version: number | null) {
    if (!asked || busy) return
    setTried(true)
    if (!price.ok || !minPickup.ok || !minThrough.ok) return
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.PUT('/v1/staff/prices/{categoryId}/{day}', {
        params: { path: { categoryId: asked.categoryId, day: asked.day } },
        body: {
          priceCents: price.value,
          minDaysPickup: minPickup.value,
          minDaysThrough: minThrough.value,
          stopSale: draft.stopSale,
          version,
        },
      }),
    )
    setBusy(false)
    if (result.ok) {
      dialogRef.current?.close()
      onSaved(asked)
      return
    }
    if (takeOver(result.problem)) return
    if (result.problem.kind === 'api' && result.problem.code === 'version_conflict') {
      const now = conflictCell(result.problem.details)
      if (now !== undefined) {
        // What was typed stays; the day as it is now stands beside it.
        setConflict(now ? { now, version: now.version } : { now: NOTHING_SET, version: null })
        onConflict()
        return
      }
    }
    setProblem(result.problem)
  }

  const priceWords = (cents: number | null) =>
    cents === null ? words.noPriceSaid : formatEuro(cents, locale)
  const minWords = (days: number | null) => (days === null ? words.none : String(days))
  /** The day now beside what was typed, line by line; a value typed wrong shows as typed. */
  const compareRows = (now: DayValues) => [
    {
      what: words.price,
      now: priceWords(now.priceCents),
      typed: price.ok ? priceWords(price.value) : draft.price,
    },
    {
      what: words.minPickupLabel,
      now: minWords(now.minDaysPickup),
      typed: minPickup.ok ? minWords(minPickup.value) : draft.minPickup,
    },
    {
      what: words.minThroughLabel,
      now: minWords(now.minDaysThrough),
      typed: minThrough.ok ? minWords(minThrough.value) : draft.minThrough,
    },
    {
      what: words.stopSaleMode,
      now: now.stopSale ? words.closed : words.open,
      typed: draft.stopSale ? words.closed : words.open,
    },
  ]
  const set = (field: keyof Draft) => (value: string | boolean) =>
    setDraft((current) => ({ ...current, [field]: value }))

  return (
    <Dialog
      dialogRef={dialogRef}
      title={
        asked
          ? format(words.dayTitle, {
              category: asked.categoryName,
              date: formatDate(locale, asked.day),
            })
          : ''
      }
      busy={busy}
      className="max-w-lg"
    >
      {asked ? (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event: FormEvent) => {
            event.preventDefault()
            if (!conflict) void save(asked.version)
          }}
        >
          <Field
            label={words.price}
            hint={words.priceHint}
            optionalLabel={words.optional}
            error={tried && !price.ok ? format(words.priceInvalid, { max: maxPrice }) : null}
          >
            {(control) => (
              <StaffInput
                {...control}
                inputMode="decimal"
                autoComplete="off"
                className="figures"
                value={draft.price}
                onChange={(event) => set('price')(event.target.value)}
              />
            )}
          </Field>
          <div className={FIELD_ROW}>
            <Field
              className={PAIRED_FIELD}
              label={words.minPickupLabel}
              hint={words.minPickupHint}
              optionalLabel={words.optional}
              error={tried && !minPickup.ok ? minError : null}
            >
              {(control) => (
                <StaffInput
                  {...control}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={DAY_RATES_MAX_MIN_DAYS}
                  step={1}
                  value={draft.minPickup}
                  onChange={(event) => set('minPickup')(event.target.value)}
                />
              )}
            </Field>
            <Field
              className={PAIRED_FIELD}
              label={words.minThroughLabel}
              hint={words.minThroughHint}
              optionalLabel={words.optional}
              error={tried && !minThrough.ok ? minError : null}
            >
              {(control) => (
                <StaffInput
                  {...control}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={DAY_RATES_MAX_MIN_DAYS}
                  step={1}
                  value={draft.minThrough}
                  onChange={(event) => set('minThrough')(event.target.value)}
                />
              )}
            </Field>
          </div>
          <label className="flex min-h-touch cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              className="size-6 accent-brand"
              checked={draft.stopSale}
              onChange={(event) => set('stopSale')(event.target.checked)}
            />
            {words.stopSaleLabel}
          </label>

          {conflict ? (
            <>
              <Notice tone="warning" live="assertive">
                <p>{words.conflict}</p>
              </Notice>
              <DataTable
                label={words.conflictTable}
                columns={[
                  { header: words.conflictWhat, cell: (row) => row.what, rowHeader: true },
                  { header: words.conflictNow, cell: (row) => row.now },
                  { header: words.conflictTyped, cell: (row) => row.typed },
                ]}
                rows={compareRows(conflict.now)}
                rowKey={(row) => row.what}
              />
            </>
          ) : null}
          <ProblemNotice problem={problem} />

          <div ref={actions} className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              className="rounded-sm"
              disabled={busy}
              onClick={() => dialogRef.current?.close()}
            >
              {conflict ? words.close : words.cancel}
            </Button>
            {conflict ? (
              <Button
                variant="staff"
                busy={busy}
                data-save-over=""
                onClick={() => void save(conflict.version)}
              >
                {words.saveOver}
              </Button>
            ) : (
              <Button type="submit" variant="staff" busy={busy}>
                {words.save}
              </Button>
            )}
          </div>
        </form>
      ) : null}
    </Dialog>
  )
}
